// src/Pages/ResetPasswordPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { turso } from '@/database/client';
import { validatePasswordStrength } from '@/lib/auth';
import { verifyResetToken as verifyTursoResetToken, resetPassword as resetPasswordWithToken } from '@/lib/passwordReset';
import bcrypt from 'bcryptjs';
import { ArrowLeft, Lock, CheckCircle, AlertTriangle } from 'lucide-react';

export default function ResetPasswordPage() {
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tursoToken, setTursoToken] = useState<string | null>(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const handlePasswordReset = async () => {
      try {
        const urlString = window.location.href;
        const hasToken = urlString.includes('?token=');
        const hasHash = urlString.includes('#');
        
        // DEBUG: Show what we're seeing
        alert(`DEBUG:\nURL has token: ${hasToken}\nURL has hash: ${hasHash}\nFull URL: ${urlString}`);
        
        console.log('[ResetPassword] Starting verification...');
        console.log('[ResetPassword] URL:', window.location.href);
        
        const urlParams = new URLSearchParams(window.location.search);
        const tursoTokenParam = urlParams.get('token');
        
        console.log('[ResetPassword] Token from URL:', tursoTokenParam);

        // First: handle WHIPT/Turso token links (?token=...)
        if (tursoTokenParam) {
          console.log('[ResetPassword] Verifying Turso token...');
          const verification = await verifyTursoResetToken(tursoTokenParam);
          console.log('[ResetPassword] Verification result:', verification);
          
          if (!verification.valid) {
            setTokenError(verification.error || 'Invalid or expired reset link.');
          } else {
            setTursoToken(tursoTokenParam);
          }
          setIsVerifying(false);
          return;
        }

        // Else: handle Supabase recovery hash (#access_token)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');

        if (type === 'recovery') {
          if (accessToken) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || ''
            });

            if (sessionError) {
              console.error('[ResetPassword] Session error:', sessionError);
              setTokenError('Invalid or expired reset link. Please request a new one.');
            }
          } else {
            setTokenError('Invalid reset link. Please request a new one.');
          }
          setIsVerifying(false);
        } else {
          // No valid reset token - redirect immediately without showing page
          setShouldRedirect(true);
          navigate('/organizer/sign-in', { replace: true });
          return;
        }
      } catch (err) {
        console.error('[ResetPassword] Failed to verify reset link:', err);
        setTokenError('Failed to verify reset link');
        setIsVerifying(false);
      }
    };

    handlePasswordReset();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const validation = validatePasswordStrength(newPassword);
    if (!validation.isValid) {
      setError(validation.errors.join('\n'));
      return;
    }

    setIsLoading(true);

    try {
      if (tursoToken) {
        // Use WHIPT/Turso token flow
        const result = await resetPasswordWithToken(tursoToken, newPassword);
        if (!result.success) {
          setError(result.error || 'Failed to reset password');
        } else {
          setSuccess(true);
        }
      } else {
        // Supabase recovery flow
        const { data: { user }, error: updateError } = await supabase.auth.updateUser({
          password: newPassword
        });
        
        if (updateError) {
          setError(updateError.message);
        } else if (user) {
          try {
            const passwordHash = await bcrypt.hash(newPassword, 12);
            await turso.execute({
              sql: 'UPDATE users SET password_hash = ? WHERE email = ?',
              args: [passwordHash, user.email]
            });
            console.log('[ResetPassword] Password updated in both Supabase and Turso');
          } catch (tursoError) {
            console.warn('[ResetPassword] Failed to update Turso password:', tursoError);
          }
          setSuccess(true);
        }
      }
    } catch (err) {
      console.error('Password reset failed:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Prevent flash by not rendering if redirecting
  if (shouldRedirect) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] flex items-center justify-center p-4">
        <div className="text-white">Redirecting to sign in...</div>
      </div>
    );
  }

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 max-w-md w-full">
          <div className="text-center text-white">
            <div className="animate-spin h-8 w-8 border-4 border-[#ffc107] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Verifying reset link...</p>
            <p className="text-sm text-gray-400 mt-2">Token from URL: {new URLSearchParams(window.location.search).get('token') ? 'Found' : 'Not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white">Invalid Reset Link</h2>
              <p className="text-slate-300">{tokenError}</p>
              <p className="text-sm text-slate-400 pt-4">
                Reset links expire after 15 minutes or can only be used once.
              </p>
            </div>

            <div className="space-y-3 pt-4">
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold transition-all"
              >
                Request New Reset Link
              </button>
              
              <button
                onClick={() => navigate('/organizer/sign-in')}
                className="w-full px-6 py-3 rounded-xl border border-slate-700 hover:border-slate-600 text-slate-300 font-semibold transition-all"
              >
                Return to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white">Password Reset Successful!</h2>
              <p className="text-slate-300">
                Your password has been changed successfully. You can now sign in with your new password.
              </p>
            </div>

            <button
              onClick={() => navigate('/organizer/sign-in')}
              className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold transition-all"
            >
              Sign In Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate('/organizer/sign-in')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Sign In</span>
        </button>

        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8">
          <div className="space-y-2 mb-6">
            <h2 className="text-2xl font-bold text-white">Reset Your Password</h2>
            <p className="text-slate-400">
              Enter your new password below.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm whitespace-pre-line">
                {error}
              </div>
            )}

            <label className="block space-y-2">
              <span className="text-sm font-medium text-slate-300">New Password</span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-11 pr-20 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none transition-colors"
                  placeholder="Enter new password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 text-sm"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-slate-300">Confirm New Password</span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none transition-colors"
                  placeholder="Confirm new password"
                  required
                  disabled={isLoading}
                />
              </div>
            </label>

            <div className="text-xs text-slate-400 space-y-1 bg-slate-800/30 p-4 rounded-xl">
              <p className="font-semibold text-slate-300">Password requirements:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>At least 8 characters</li>
                <li>One uppercase letter (A-Z)</li>
                <li>One lowercase letter (a-z)</li>
                <li>One number (0-9)</li>
                <li>One special character (!@#$%^&*...)</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Resetting Password...</span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  <span>Reset Password</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
